import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import api from "../../../../services/api";

export default function ChatInterface({ user }) {
  const [chatMessage, setChatMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [selectedSession, setSelectedSession] = useState("");
  const [kpis, setKpis] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [kpiMeasurements, setKpiMeasurements] = useState([]);
  const messagesEndRef = useRef(null);

  const currentUser = useSelector((state) => state.Auth.user);

  const getCurrentWeek = () => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const daysSinceStartOfYear = Math.floor((now - startOfYear) / (24 * 60 * 60 * 1000));
    return Math.ceil((daysSinceStartOfYear + startOfYear.getDay() + 1) / 7);
  };

  useEffect(() => {
    getMessages();
    fetchKpis();
    fetchTasks();
  }, [user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  async function getMessages() {
    try {
      const { data, ok } = await api.post("/meeting_message/search", { user_id: user._id, sortby: "createdAt", limit: 100, });
      if (!ok) return toast.error("Error loading messages");
      setMessages(data);
    } catch (error) {
      toast.error("Error loading messages");
    }
  }

  async function addMessage(message) {
    try {
      const { ok } = await api.post("/meeting_message", {
        text: message,
        user_id: user._id,
        user_name: user.name,
        user_avatar: user.avatar,
      });
      if (!ok) return toast.error("Error saving message");
      await getMessages();
    } catch (error) {
      toast.error("Error saving message");
    }
  }


  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }
  

  async function handleSendMessage() {
    if (!chatMessage.trim() || isLoadingChat) return;

    const userMessage = chatMessage.trim();
    setChatMessage("");
    setIsLoadingChat(true);

    try {
      await addMessage(userMessage);

      const { ok } = await api.post("/meeting_message/chat", {
        message: userMessage,
        user_id: user._id,
        user_name: user.name,
        user_avatar: user.avatar,
        author_name: "IA",
      });

      if (!ok) return toast.error("Error communicating with Claude");
      await getMessages();
    } catch (error) {
      toast.error("Error communicating with Claude");
    } finally {
      setIsLoadingChat(false);
    }
  }

  async function fetchKpis() {
    try {
    const { data, ok } = await api.post("/mgt_kpi/search", { user_id: user._id, limit: 10 });
    if (!ok) return toast.error("Error loading KPIs");
    setKpis(data);
    fetchKpiMeasurements(data);
    } catch (e) {
      toast.error("Error loading KPIs");
    }
  }

  async function fetchTasks() {
    try {
    const { data, ok } = await api.post("/mgt_task/search", { user_id: user._id, limit: 10 });
    if (!ok) return toast.error("Error loading tasks");
    setTasks(data);
  } catch (e) {
    toast.error("Error loading tasks");
  }
}

async function fetchKpiMeasurements(kpis) {
  try {
    const currentWeek = getCurrentWeek();
    const lastWeek = currentWeek - 1;
    const allMeasurements = [];
    
    for (const kpi of kpis) {
      const { data, ok } = await api.get(`/mgt_kpi_measurement/kpi/${kpi._id}/weekly-data`);
      if (ok && data) {
        const filteredMeasurements = data.filter(measurement =>  measurement.week === currentWeek || measurement.week === lastWeek );
        allMeasurements.push(...filteredMeasurements.map(measurement => ({ ...measurement, kpi_id: kpi._id })));
      }
    }
    setKpiMeasurements(allMeasurements);
  } catch (e) {
    toast.error("Error loading KPI measurements");
  }
}

  async function getSessionPrompt(sessionType) {
    const currentWeek = getCurrentWeek();
    
    let sessionPrompt = "";
    switch (sessionType) {
      case "weekly_planning":
        sessionPrompt = `Help me plan the week for ${user.name}. Let's review their current KPIs and set weekly goals.`;
        break;
      case "mid_week_review":
        sessionPrompt = `Let's do a mid-week check-in for ${user.name}. How is the week going? What adjustments are needed?`;
        break;
      case "end_of_week":
        sessionPrompt = `Let's do an end-of-week review for ${user.name}. Let's analyze achievements, learnings and prepare for next week.`;
        break;
      default:
        break;
    }

    let contextInfo = `\n\nCurrent Week: ${currentWeek}`;
    if (user.main_objective && user.main_objective.trim()) contextInfo += `\n\nMain Objective:\n${user.main_objective.trim()}`;

    let kpiText = "";
    if (Array.isArray(kpis) && kpis.length > 0) {
      kpiText += "\n\nKPIs:";
      for (let i = 0; i < kpis.length; i++) { 
        const kpi = kpis[i];
        const weekly = kpiMeasurements.filter((m) => m.kpi_id === kpi._id);
        kpiText += `\n${kpi.description || "No description"}:`;
        if (weekly.length > 0) {
          for (let j = 0; j < weekly.length; j++) {
            const m = weekly[j];
            const weekLabel = m.week === currentWeek ? "Current Week" : "Last Week";
            kpiText += `\n  ${weekLabel} (Week ${m.week}): ${m.value}/${kpi.target || 'No target'}`;
          }
        } else {
          kpiText += `\n  No measurements available for current and last week`;
        }
      }
    }

    let tasksText = "";
    if (Array.isArray(tasks) && tasks.length > 0) {
      tasksText += "\n\nTasks:";
      for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i];
        const status = task.completed ? "Completed" : "Not Completed";
        const deadline = task.deadline ? ` | Deadline: ${new Date(task.deadline).toLocaleDateString()}` : "";
        const priority = task.priority ? ` | Priority: ${task.priority}` : "";
        
        tasksText += `\n• ${task.name}`;
        if (task.description && task.description !== task.name) {
          tasksText += `\n  Description: ${task.description}`;
        }
        tasksText += `\n  Status: ${status}${deadline}${priority}`;
        if (i < tasks.length - 1) tasksText += "\n";
      }
    }

    let finalContext = contextInfo;
    if (kpiText) finalContext += `${finalContext ? "\n" : ""}${kpiText}`;
    if (tasksText) finalContext += `${finalContext ? "\n" : ""}${tasksText}`;
    if (finalContext) return `${sessionPrompt}\n\n${finalContext}`;
    return sessionPrompt;
  }

  async function handleSessionSelect(e) {
    const value = e.target.value;
    setSelectedSession(value);
    if (value) {
      const prompt = await getSessionPrompt(value);
      setChatMessage(prompt);
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="flex h-full bg-gradient-to-br from-slate-50 to-blue-50/30">
      <div className="w-full flex flex-col h-full max-w-7xl mx-auto">
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="bg-white/80 backdrop-blur-sm border border-slate-200/60 p-8 sm:p-12 rounded-2xl max-w-md sm:max-w-lg shadow-xl shadow-slate-200/50">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-blue-500/25">
                    <div className="w-8 h-8 text-white flex items-center justify-center text-lg font-bold">AI</div>
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 border-3 border-white rounded-full"></div>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-4">Ready to assist you</h2>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                  Start a conversation with Claude AI to get personalized insights and recommendations for managing {user.name}.
                </p>
                <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <p className="text-xs sm:text-sm text-blue-700 font-medium">💡 Tip: Use the Quick Session dropdown to get started with common tasks!</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3 max-w-3xl mx-auto">
              {messages.map((message, index) => {
                const isIA = message.author_name === "IA";
                const isCurrentUser = message.author_id === currentUser._id;
                return (
                  <div key={message._id} className={`flex ${isIA ? "justify-start" : "justify-end"} animate-fadeIn`} style={{ animationDelay: `${index * 0.1}s` }}>
                    <div className={`flex gap-2 max-w-[85%] sm:max-w-[75%] ${isIA ? "flex-row" : "flex-row-reverse"}`}>
                      <div className="flex-shrink-0">
                        {isIA ? (
                          <div className="w-6 h-6 sm:w-7 sm:h-7 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-center rounded-lg text-xs font-semibold shadow-lg shadow-blue-500/25">
                            AI
                          </div>
                        ) : (
                          <img
                            src={message.author_avatar || "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y"}
                            alt={message.author_name}
                            className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg object-cover border-2 border-white shadow-md"
                          />
                        )}
                      </div>
                      <div className={`flex flex-col ${isIA ? "items-start" : "items-end"} max-w-full`}>
                        <div
                          className={`px-3 py-2 rounded-xl max-w-none shadow-sm ${
                            isIA
                              ? "bg-white/90 backdrop-blur-sm text-slate-800 border border-slate-200/60 shadow-slate-200/50"
                              : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-blue-500/25"
                          }`}>
                          <p className="text-xs sm:text-sm leading-relaxed whitespace-pre-wrap">{message.text}</p>
                        </div>
                        <div className={`text-xs text-slate-400 mt-1 px-1 ${isIA ? "text-left" : "text-right"}`}>
                          <span className="font-medium">{isIA ? "Claude AI" : message.author_name}</span>
                          <span className="mx-1">•</span>
                          <span>{formatDate(message.created_at || message.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {isLoadingChat && (
                <div className="flex justify-start animate-fadeIn">
                  <div className="flex gap-2 max-w-[75%]">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-center rounded-lg text-xs font-semibold shadow-lg shadow-blue-500/25">
                      AI
                    </div>
                    <div className="bg-white/90 backdrop-blur-sm text-slate-800 border border-slate-200/60 px-3 py-2 rounded-xl shadow-sm">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce"></div>
                          <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
                          <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                        </div>
                        <span className="text-xs text-slate-600">Claude is thinking...</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Compact Input Area */}
        <div className="border-t border-slate-200/60 bg-white/80 backdrop-blur-sm flex-shrink-0">
          <div className="px-3 pt-2 pb-0 sm:px-3 sm:pt-3 sm:pb-0">
            {/* Compact Message Input */}

            <div className="hidden sm:block">
              <select
                value={selectedSession}
                onChange={handleSessionSelect}
                className="border border-slate-300 rounded-lg my-1 w-28 px-3 py-1.5 text-xs bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all duration-200"
                disabled={isLoadingChat}>
                <option value="">Quick Session</option>
                <option value="weekly_planning">📅 Weekly</option>
                <option value="mid_week_review">📊 Mid-Week</option>
                <option value="end_of_week">✅ End Week</option>
              </select>
            </div>

            <div className="flex gap-1.5 items-center justify-center">
              {/* Quick Session Selector - Inline */}

              <div className="flex-1">
                <textarea
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={`Ask Claude about ${user.name}...`}
                  className="w-full p-2 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all duration-200 text-xs bg-white resize-none placeholder:text-slate-400"
                  style={{ fieldSizing: "content" }}
                  rows={1}
                  disabled={isLoadingChat}
                />
              </div>

              <button
                onClick={handleSendMessage}
                disabled={isLoadingChat || !chatMessage.trim()}
                className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm">
                {isLoadingChat ? (
                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                ) : (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
              </button>
            </div>

            {/* Mobile Quick Session - Below input */}
            <div className="sm:hidden mt-1.5">
              <select
                value={selectedSession}
                onChange={handleSessionSelect}
                className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-xs bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all duration-200"
                disabled={isLoadingChat}>
                <option value="">Quick Session Templates</option>
                <option value="weekly_planning">📅 Weekly Planning</option>
                <option value="mid_week_review">📊 Mid-Week Review</option>
                <option value="end_of_week">✅ End of Week</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
